import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Gauge, Sparkle, Robot, ListChecks, CalendarBlank, Images, EnvelopeSimple,
  MagnifyingGlass, Megaphone, ChartBar, CurrencyDollar, Package, UsersThree,
  FileText, PlugsConnected, CaretDown, Plus, Coffee, List, X, Flame, Check,
  Wrench, Barbell, Handshake, Browsers, GearSix, SidebarSimple, CaretRight, Copy,
  House, SquaresFour, ImageSquare, CheckSquareOffset, Binoculars, Storefront, Strategy, CalendarStar, ChartLineUp, Rocket, Buildings, SignOut, UserCircle, ShieldCheck, ShareNetwork, Scroll, TrendUp, FilmSlate, ClockCounterClockwise, LinkedinLogo, Target, Funnel, ClipboardText, UserPlus,
} from "@phosphor-icons/react";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common/Logo";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { createClient, getBillingPlans } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const NAV = [
  {
    section: "Dashboard", items: [
      { to: "/owner", label: "Owner Dashboard", icon: Buildings, seesAllOnly: true },
      { to: "/", label: "Executive Dashboard", icon: House, end: true },
      { to: "/dashboard", label: "Widget Board", icon: SquaresFour },
      { to: "/analytics", label: "Analytics", icon: ChartBar },
    ],
  },
  {
    section: "AI Command",
    items: [
      { to: "/employees", label: "AI Employees", icon: UsersThree },
      { to: "/ai-manager", label: "AI Manager", icon: Robot },
      { to: "/director", label: "AI Marketing Director", icon: Strategy },
      { to: "/onboarding", label: "AI Onboarding", icon: UserPlus },
      { to: "/monthly", label: "AI Monthly Planner", icon: CalendarStar },
      { to: "/strategy", label: "Content Strategy", icon: Target },
      { to: "/business-audit", label: "AI Business Audit", icon: ClipboardText },
      { to: "/revenue", label: "AI Revenue Manager", icon: TrendUp },
      { to: "/comms", label: "AI Communications", icon: EnvelopeSimple },
      { to: "/ai-studio", label: "AI Content Studio", icon: Sparkle },
      { to: "/ai-image", label: "AI Image Studio", icon: ImageSquare },
      { to: "/social", label: "AI Social Manager", icon: ShareNetwork },
      { to: "/video-studio", label: "AI Video Studio", icon: FilmSlate },
      { to: "/brands", label: "Brand Manager", icon: Flame },
      { to: "/sales-manager", label: "AI Sales Manager", icon: Handshake },
      { to: "/seo-manager", label: "AI SEO Manager", icon: MagnifyingGlass },
      { to: "/ad-manager", label: "AI Advertising Manager", icon: Megaphone },
      { to: "/website-optimizer", label: "AI Website Optimizer", icon: Browsers },
    ],
  },
  {
    section: "Marketing",
    items: [
      { to: "/calendar", label: "Content Calendar", icon: CalendarBlank },
      { to: "/library", label: "Content Library", icon: Images },
      { to: "/email", label: "Email & SMS", icon: EnvelopeSimple },
      { to: "/seo", label: "SEO Planner", icon: MagnifyingGlass },
      { to: "/promotions", label: "Promotions", icon: Megaphone },
      { to: "/competitors", label: "Competitors", icon: Binoculars },
    ],
  },
  {
    section: "Performance",
    items: [
      { to: "/ga4", label: "Google Analytics", icon: ChartLineUp },
      { to: "/ads", label: "Meta & Google Ads", icon: Megaphone },
      { to: "/product-intel", label: "Product Intelligence", icon: Package },
      { to: "/ad-budget", label: "Ad Budget", icon: CurrencyDollar },
      { to: "/roas", label: "ROAS Tracker", icon: ChartBar },
      { to: "/products", label: "Products", icon: Package },
      { to: "/customers", label: "Customers & Subs", icon: UsersThree },
    ],
  },
  {
    section: "Workflow",
    items: [
      { to: "/approvals", label: "Approval Queue", icon: CheckSquareOffset },
      { to: "/campaign-history", label: "Campaign History", icon: ClockCounterClockwise },
      { to: "/scheduler", label: "Marketing Calendar", icon: CalendarStar },
      { to: "/linkedin", label: "LinkedIn Center", icon: LinkedinLogo },
      { to: "/media", label: "Media Library", icon: ImageSquare },
      { to: "/conversion", label: "Conversion Optimizer", icon: Storefront },
      { to: "/audit", label: "Audit Logs", icon: Scroll, seesAllOnly: true },
    ],
  },
  {
    section: "Agency",
    items: [
      { to: "/agency", label: "Agency Command", icon: ShieldCheck, seesAllOnly: true },
      { to: "/leads", label: "Leads & Growth", icon: Funnel, superOnly: true },
      { to: "/templates", label: "Templates", icon: Copy },
      { to: "/brand-assets", label: "Brand Assets", icon: Images },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/shopify", label: "Shopify", icon: Storefront },
      { to: "/integrations", label: "Integrations", icon: PlugsConnected },
      { to: "/connections", label: "Connections Health", icon: PlugsConnected },
      { to: "/reports", label: "Weekly Reports", icon: FileText },
      { to: "/billing", label: "Billing", icon: CurrencyDollar },
      { to: "/usage", label: "Billing & Usage", icon: Gauge },
      { to: "/usage-control", label: "Usage Control", icon: ShieldCheck, superOnly: true },
      { to: "/launch", label: "Launch Readiness", icon: Rocket },
      { to: "/readiness", label: "System Readiness", icon: ShieldCheck },
      { to: "/deploy-validation", label: "Deployment Validation", icon: ShieldCheck, superOnly: true },
      { to: "/profile", label: "Settings", icon: GearSix },
    ],
  },
  {
    section: "Plan",
    items: [
      { to: "/plan", label: "30-Day Plan", icon: ListChecks },
      { to: "/checklist", label: "Marketing Checklist", icon: ListChecks },
    ],
  },
];

const EXPANDED_KEY = "vozelto.nav.expanded";
const COMPACT_KEY = "vozelto.nav.compact";
const DEFAULT_EXPANDED = { Dashboard: true, "AI Command": true, Workflow: true, System: true };

function NewClientDialog() {
  const { refresh, setActive } = useClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", brand_voice: "", website: "" });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    const c = await createClient(form);
    await refresh();
    setActive(c.id);
    setOpen(false);
    setForm({ name: "", industry: "", brand_voice: "", website: "" });
    toast.success(`${c.name} added`);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-client-btn" className="flex items-center gap-2 w-full px-2 py-2 text-sm text-[#A3A3A3] hover:text-white">
          <Plus size={16} /> Add new client
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#14141F] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-head">Add New Client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-caps">Brand name</Label>
            <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
          <div><Label className="label-caps">Industry</Label>
            <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
          <div><Label className="label-caps">Website</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
          <div><Label className="label-caps">Brand voice / positioning</Label>
            <Textarea value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" rows={3} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="save-client-btn" onClick={save} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Create client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function clientIcon(c) {
  const s = `${c?.name || ""} ${c?.industry || ""}`.toLowerCase();
  if (/coffee|ember|cafe|bean|roast/.test(s)) return Coffee;
  if (/property|maintenance|turnkey|repair|build|contractor|home|real estate|handyman/.test(s)) return Wrench;
  if (/fitness|gym|enough|health|train|workout|wellness|nutrition/.test(s)) return Barbell;
  return Storefront;
}

// Official workspace logo when set (clients.logo_url), otherwise the category icon.
function ClientMark({ c, size = 18, className = "" }) {
  if (c?.logo_url) {
    return <img src={c.logo_url} alt={`${c.name} logo`} data-testid={`client-logo-${c.id}`}
      className={`rounded object-contain ${className}`} style={{ width: size + 6, height: size + 6 }} />;
  }
  const Icon = clientIcon(c);
  return <Icon size={size} className={className || "brand-text"} weight="fill" />;
}

function ClientSwitcher() {
  const { clients, activeClient, activeId, setActive, canSwitch } = useClient();
  const { seesAll } = useAuth();
  const navigate = useNavigate();
  const switchTo = (id) => {
    if (id !== activeId) setActive(id);
    navigate("/"); // open that business's Executive Dashboard
  };
  // Client-scoped users with a single workspace get a static brand chip (no switching).
  if (!canSwitch) {
    return (
      <div data-testid="client-static" className="flex items-center gap-3 w-full rounded-lg border border-[#6B3DFF]/30 bg-[#10101A] px-3 py-2.5">
        <div className="h-8 w-8 rounded-md bg-[#6B3DFF]/15 flex items-center justify-center overflow-hidden">
          <ClientMark c={activeClient} size={18} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-semibold truncate">{activeClient?.name || "Workspace"}</div>
          <div className="text-[11px] text-[#7a7a7a] truncate mt-0.5 leading-tight">{activeClient?.industry || "Workspace"}</div>
        </div>
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="client-switcher" className="flex items-center gap-3 w-full rounded-lg border border-[#6B3DFF]/30 bg-[#10101A] px-3 py-2.5 hover:border-[#6B3DFF]/50 transition-all">
          <div className="h-8 w-8 rounded-md bg-[#6B3DFF]/15 flex items-center justify-center overflow-hidden">
            <ClientMark c={activeClient} size={18} />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm font-semibold truncate">{activeClient?.name || "Select workspace"}</div>
            <div className="text-[11px] text-[#7a7a7a] truncate mt-0.5 leading-tight">{activeClient?.industry || "Workspace"}</div>
          </div>
          <CaretDown size={14} className="text-[#7a7a7a]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 bg-[#14141F] border-white/10 text-white">
        <DropdownMenuLabel className="label-caps">Workspaces</DropdownMenuLabel>
        {clients.map((c) => {
          const active = c.id === activeId;
          return (
            <DropdownMenuItem key={c.id} data-testid={`client-option-${c.id}`} onClick={() => switchTo(c.id)}
              className={`cursor-pointer focus:bg-white/5 ${active ? "bg-[#6B3DFF]/10" : ""}`}>
              <span className="mr-2 flex items-center"><ClientMark c={c} size={14} className={active ? "brand-text" : "text-[#7a7a7a]"} /></span>
              <span className={active ? "text-white font-semibold" : ""}>{c.name}</span>
              {active && <Check size={14} className="brand-text ml-auto" weight="bold" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuItem data-testid="client-option-combined" onClick={() => navigate("/owner")}
          className="cursor-pointer focus:bg-white/5">
          <Buildings size={14} className="text-[#9a9a9a] mr-2" /> <span>Combined Business view</span>
        </DropdownMenuItem>
        {seesAll && (<>
          <DropdownMenuSeparator className="bg-white/10" />
          <NewClientDialog />
        </>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, seesAll } = useAuth();
  const handleLogout = async () => { await logout(); navigate("/"); };
  const billingQ = useQuery({ queryKey: ["billing-mode"], queryFn: getBillingPlans, enabled: !!isAdmin, staleTime: 60000 });
  const billingLive = billingQ.data?.live;

  // Collapsible groups (persisted) + compact icon-only mode (persisted)
  const [expanded, setExpanded] = useState(() => {
    try { return { ...DEFAULT_EXPANDED, ...JSON.parse(localStorage.getItem(EXPANDED_KEY) || "{}") }; }
    catch { return { ...DEFAULT_EXPANDED }; }
  });
  const [compact, setCompact] = useState(() => localStorage.getItem(COMPACT_KEY) === "1");
  const toggleGroup = (section) => setExpanded((s) => {
    const n = { ...s, [section]: !s[section] };
    localStorage.setItem(EXPANDED_KEY, JSON.stringify(n));
    return n;
  });
  const toggleCompact = () => setCompact((c) => { localStorage.setItem(COMPACT_KEY, c ? "0" : "1"); return !c; });
  // Auto-expand the group containing the active route
  useEffect(() => {
    const g = NAV.find((grp) => grp.items.some((it) => it.end ? location.pathname === it.to : location.pathname.startsWith(it.to) && it.to !== "/"));
    if (g && !expanded[g.section]) setExpanded((s) => ({ ...s, [g.section]: true }));
  }, [location.pathname]); // eslint-disable-line

  const roleLabel = {
    super_admin: "Super Admin", agency_admin: "Agency Admin", agency_staff: "Agency Staff",
    workspace_owner: "Workspace Owner", marketing_manager: "Marketing Manager", marketing_staff: "Marketing Staff",
    office_staff: "Office Staff", sales: "Sales", read_only: "Read Only",
    client_owner: "Client Owner", client_staff: "Client Staff",
  }[user?.role] || "User";

  const visible = (item) => (!item.adminOnly || isAdmin) && (!item.seesAllOnly || seesAll)
    && (!item.superOnly || user?.role === "super_admin");

  const renderItem = (item, isCompact) => {
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} end={item.end}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
        onClick={() => setMobileOpen(false)}
        title={isCompact ? item.label : undefined}
        className={({ isActive }) =>
          `flex items-center rounded-lg text-sm transition-colors ${
            isCompact ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2"
          } ${
            isActive ? "bg-[#6B3DFF]/12 text-white border border-[#6B3DFF]/25"
              : "text-[#9a9a9a] hover:text-white hover:bg-white/5 border border-transparent"
          }`
        }>
        {({ isActive }) => (
          <>
            <Icon size={18} weight={isActive ? "fill" : "regular"} className={isActive ? "brand-text" : ""} />
            {!isCompact && <span className="truncate">{item.label}</span>}
          </>
        )}
      </NavLink>
    );
  };

  const SidebarContent = (isCompact) => (
    <aside className={`${isCompact ? "w-[72px]" : "w-64"} shrink-0 h-full bg-[#0a0a0a] border-r border-white/10 flex flex-col transition-all duration-200`}>
      <div className={`${isCompact ? "px-3" : "px-5"} py-4 border-b border-white/10`}>
        <div className={`flex items-center ${isCompact ? "justify-center" : "justify-between"} mb-3`}>
          {isCompact ? <Logo variant="icon" height={30} /> : <Logo variant="horizontal" height={34} className="ml-0.5" />}
          {!isCompact && (
            <button data-testid="sidebar-compact-toggle" onClick={toggleCompact} title="Compact sidebar"
              className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-[#7a7a7a] hover:text-white hover:bg-white/5">
              <SidebarSimple size={16} />
            </button>
          )}
        </div>
        {isCompact ? (
          <button data-testid="sidebar-expand-toggle" onClick={toggleCompact} title="Expand sidebar"
            className="flex h-9 w-full items-center justify-center rounded-lg border border-[#6B3DFF]/30 bg-[#10101A] text-[#9a9a9a] hover:text-white hover:border-[#6B3DFF]/50">
            <CaretRight size={15} />
          </button>
        ) : <ClientSwitcher />}
      </div>
      <nav className={`flex-1 overflow-y-auto ${isCompact ? "px-2.5" : "px-3"} py-3 ${isCompact ? "space-y-1" : "space-y-1.5"}`}>
        {NAV.map((group) => {
          const items = group.items.filter(visible);
          if (!items.length) return null;
          const open = !!expanded[group.section];
          if (isCompact) {
            return (
              <div key={group.section}>
                <div className="mx-2 my-2 border-t border-white/8" title={group.section} />
                <div className="space-y-0.5">{items.map((it) => renderItem(it, true))}</div>
              </div>
            );
          }
          return (
            <div key={group.section}>
              <button data-testid={`nav-group-${group.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => toggleGroup(group.section)}
                className="flex items-center justify-between w-full px-2 py-1.5 rounded-md group hover:bg-white/[0.03]">
                <span className="label-caps group-hover:text-[#cfcfcf] transition-colors">{group.section}</span>
                <CaretDown size={12} className={`text-[#5a5a5a] group-hover:text-[#9a9a9a] transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${open ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="space-y-0.5 mt-0.5 mb-1.5">{items.map((it) => renderItem(it, false))}</div>
              </div>
            </div>
          );
        })}
      </nav>
      <div className={`${isCompact ? "px-2" : "px-3"} py-3 border-t border-white/10`}>
        {!isCompact && isAdmin && billingQ.data && (
          <div data-testid="billing-mode-chip"
            className={`flex items-center gap-1.5 mb-2 px-2.5 py-1.5 rounded-md text-[10px] font-semibold tracking-wide border ${billingLive ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-amber-500/10 border-amber-500/30 text-amber-400"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${billingLive ? "bg-emerald-400" : "bg-amber-400"}`} />
            BILLING: {billingLive ? "LIVE" : "TEST"}
          </div>
        )}
        <button data-testid="profile-link" onClick={() => navigate("/profile")} title={isCompact ? "Profile" : undefined}
          className={`flex items-center ${isCompact ? "justify-center" : "gap-2.5"} px-2 py-2 w-full rounded-lg hover:bg-white/5 transition-colors text-left`}>
          <div className="h-8 w-8 rounded-full bg-[#6B3DFF]/15 flex items-center justify-center shrink-0">
            <UserCircle size={20} className="brand-text" weight="fill" />
          </div>
          {!isCompact && (
            <div className="flex-1 min-w-0" data-testid="current-user">
              <div className="text-sm font-semibold truncate">{user?.display_name || user?.first_name || user?.name || user?.email}</div>
              <div className="text-[10px] text-[#7a7a7a] truncate">{roleLabel}</div>
            </div>
          )}
        </button>
        <button data-testid="logout-btn" onClick={handleLogout} title={isCompact ? "Sign out" : undefined}
          className={`flex items-center ${isCompact ? "justify-center" : "gap-2"} w-full px-3 py-2 mt-1 rounded-lg text-sm text-[#9a9a9a] hover:text-white hover:bg-white/5 transition-colors`}>
          <SignOut size={16} /> {!isCompact && "Sign out"}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="grain-bg flex h-screen overflow-hidden bg-[#0D0D14] text-white">
      <div className="hidden lg:block relative z-10">{SidebarContent(compact)}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full z-50">{SidebarContent(false)}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden relative z-10">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0a0a0a]">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}><List size={24} /></button>
          <Logo variant="horizontal" height={28} showTagline={false} />
          <div className="w-6" />
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
        <UpgradeDialog />
      </div>
    </div>
  );
}
